const IPSTACK_URL = "https://api.ipstack.com/check";

// Shared with the old theme, so shoppers it already located keep their
// coordinates. It wrote "lat, lng" and never expired them.
const STORAGE_KEY = "userCoords";

// Kept once settled: a failing key or a blocked request is tried once per
// page, not on every open.
let request = null;

/**
 * @param {string} text "lat, lng"
 * @returns {{lat: number, lng: number}|null}
 */
export function parseCoords(text) {
  const [lat, lng] = String(text).split(",").map(Number);

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function readSaved() {
  try {
    return parseCoords(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function save({ lat, lng }) {
  try {
    localStorage.setItem(STORAGE_KEY, `${lat}, ${lng}`);
  } catch {}
}

/**
 * The shopper's approximate location from their IP address, through ipstack.
 * Looked up once per browser; an unreadable saved value (the old theme saved
 * "undefined, undefined" when a lookup failed) is looked up again.
 *
 * @param {string} [apiKey] Without one, only a saved location is used.
 * @returns {Promise<{lat: number, lng: number}|null>} null when it can't be
 *   found.
 */
export function getUserLocation(apiKey) {
  const saved = readSaved();
  if (saved) return Promise.resolve(saved);
  if (!apiKey) return Promise.resolve(null);

  if (!request) {
    const url = new URL(IPSTACK_URL);
    url.searchParams.set("access_key", apiKey);
    url.searchParams.set("fields", "latitude,longitude");

    // ipstack reports its errors with a 200 and `success: false`, which
    // carries no coordinates.
    request = fetch(url)
      .then((response) => response.json())
      .then(({ latitude, longitude }) => {
        const coords = parseCoords(`${latitude},${longitude}`);
        if (coords) save(coords);
        return coords;
      })
      .catch(() => null);
  }

  return request;
}

/**
 * @returns {number} Kilometres between two points, along the earth's surface.
 */
export function distanceBetween(from, to) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(dLng / 2) ** 2;

  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
